/**
 * Get hotel loyalty program member ID for an employee based on the hotel chain name
 *
 * @param chainName - The hotel chain name from the offer
 * @param employee - The employee data from the ME endpoint
 * @returns The member ID if found, empty string otherwise
 */
export function getEmployeeHotelLoyaltyMemberId(
  chainName: string | undefined,
  employee?: App.BusinessTraveller.EmployeeFromMeEndpoint,
): string {
  if (!employee?.employeeHotelLoyaltyPrograms || !chainName) {
    return ''
  }

  const loyaltyProgram = employee.employeeHotelLoyaltyPrograms.find(
    // Trim chain name as it is from Expedia supplier which could contain whitespace
    // The program name is stored by our database, it does not contain whitespace
    (program) => program.program.toLowerCase() === chainName.trim().toLowerCase(),
  )

  return loyaltyProgram ? loyaltyProgram.memberNumber : ''
}

/**
 * Get hotel loyalty program member ID checkout field for pre-filling traveller forms
 * Maps bedbank items to their corresponding hotel chain loyalty programs
 *
 * @param employee - The employee data from the ME endpoint
 * @param bedbankItems - Array of bedbank hotel items in the cart
 * @param getOffer - Function to get full offer details by offer ID
 * @returns Object with indexed hotelLoyaltyProgramMemberId fields if applicable
 */
export function getHotelLoyaltyCheckoutFields(
  employee: App.BusinessTraveller.EmployeeFromMeEndpoint | undefined,
  bedbankItems: Array<App.Checkout.BedbankHotelItem>,
  getOffer: (offerId: string) => App.Offer | App.BedbankOffer | App.CruiseOffer | App.Tours.TourV2Offer | undefined,
): Record<string, string> {
  if (!employee?.employeeHotelLoyaltyPrograms || bedbankItems.length === 0) {
    return {}
  }

  // There is only one hotel loyalty program member ID per employee, so we can just use the first bedbank item's offer ID
  // In the future, we may want to allow users to decide which bedbank item to use for the hotel loyalty program member ID
  const offer = getOffer(bedbankItems[0].offerId) as App.BedbankOffer | undefined

  if (!offer) {
    return {}
  }

  const chainName = offer?.property?.chainName
  const memberId = getEmployeeHotelLoyaltyMemberId(chainName, employee)

  return memberId ? { hotelLoyaltyProgramMemberId: memberId } : {}
}
